import React from 'react';
import { Lock as LockIcon } from 'lucide-react';

/**
 * V2 form primitives — Input, Textarea, Select, Label, FieldGroup.
 *
 * RC-94a — Foundation primitives missing from the V2 design system. Every
 * V2 modal up to this point inlined raw <input>/<select>/<textarea> with
 * hand-rolled Tailwind, which guaranteed visual drift (e.g. RC-94's
 * Templates Edit modal — Type field appeared disabled while Category
 * looked editable, despite both being the same element type). Centralizing
 * these in one tokenized primitive set kills the entire class of bug.
 *
 * Design contract — all controls share:
 *   - V2 token surface/border/ink classes (no hand-rolled hex)
 *   - `disabled` AND `readOnly` are distinguished:
 *       · disabled → muted bg + opacity, not focusable (browser default)
 *       · readOnly → muted bg + lock icon + tooltip explaining WHY
 *     This solves the RC-94 confusion where operators saw a greyed Type
 *     field with no signal of why they couldn't edit it. ReadOnly is for
 *     "intentionally immutable", disabled is for "wait, this isn't ready".
 *   - error state (red border + below-field message)
 *   - hint text below the control (muted, sm)
 *   - consistent height per size (sm:28px, md:32px, lg:40px) so a row of
 *     mixed Input/Select/Button always aligns.
 *
 * Sizes: sm, md (default), lg.
 *
 * Usage:
 *   <FieldGroup label="Name" required>
 *     <Input value={x} onChange={e => setX(e.target.value)} placeholder="…" />
 *   </FieldGroup>
 *
 *   <FieldGroup label="Type" hint="Immutable after creation.">
 *     <Input value={type} readOnly readOnlyReason="Type can't change post-create." />
 *   </FieldGroup>
 *
 *   <FieldGroup label="Body" error={bodyError}>
 *     <Textarea rows={8} value={body} onChange={e => setBody(e.target.value)} mono />
 *   </FieldGroup>
 *
 *   <FieldGroup label="Category">
 *     <Select value={cat} onChange={e => setCat(e.target.value)}>
 *       <option value="">Select…</option>
 *       <option value="alert">Alert</option>
 *     </Select>
 *   </FieldGroup>
 */

// ─── Shared classes ────────────────────────────────────────────────
const SHARED_BASE =
  'v2-focus-ring w-full text-v2-ink ' +
  'border rounded-v2-md ' +
  'transition-[background-color,border-color,box-shadow] duration-v2 ease-v2 ' +
  'placeholder:text-v2-ink-muted ' +
  // disabled is the harsh "not yet ready" state — heavier opacity
  'disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-v2-muted ' +
  // readOnly is the softer "intentionally locked" state — muted bg, full opacity
  'read-only:bg-v2-muted read-only:cursor-default';

const SIZE_CLASS = {
  sm: 'text-[12px] px-2.5 h-7',
  md: 'text-[13px] px-3 h-8',
  lg: 'text-[14px] px-3.5 h-10',
};

const TEXTAREA_SIZE_CLASS = {
  sm: 'text-[12px] px-2.5 py-1.5',
  md: 'text-[13px] px-3 py-2',
  lg: 'text-[14px] px-3.5 py-2.5',
};

const stateClass = (error, readOnly) => {
  if (error) {
    return 'bg-v2-surface border-v2-brand focus:border-v2-brand focus:shadow-v2-xs';
  }
  if (readOnly) {
    // Already styled via read-only: utilities; just ensure border tone is calm.
    return 'bg-v2-surface border-v2-border';
  }
  return 'bg-v2-surface border-v2-border hover:border-v2-border-strong focus:border-v2-action focus:shadow-v2-xs';
};

// ─── Input ─────────────────────────────────────────────────────────
export function Input({
  size = 'md',
  error = false,
  readOnly = false,
  readOnlyReason,
  disabled = false,
  className = '',
  type = 'text',
  ...rest
}) {
  const cls = `${SHARED_BASE} ${SIZE_CLASS[size]} ${stateClass(error, readOnly)} ${className}`.trim();
  // If readOnly, wrap so a lock icon can sit at the right edge.
  if (readOnly) {
    return (
      <div className="relative">
        <input
          type={type}
          readOnly
          disabled={disabled}
          aria-readonly="true"
          title={readOnlyReason || 'This field is read-only.'}
          className={`${cls} pr-7`}
          {...rest}
        />
        <LockIcon
          aria-hidden="true"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none"
          style={{ width: 12, height: 12 }}
        />
      </div>
    );
  }
  return (
    <input
      type={type}
      disabled={disabled}
      className={cls}
      {...rest}
    />
  );
}

// ─── Textarea ──────────────────────────────────────────────────────
export function Textarea({
  size = 'md',
  error = false,
  readOnly = false,
  readOnlyReason,
  disabled = false,
  className = '',
  mono = false,
  rows = 4,
  ...rest
}) {
  const fontCls = mono ? 'font-mono' : '';
  const cls = `${SHARED_BASE} ${TEXTAREA_SIZE_CLASS[size]} ${stateClass(error, readOnly)} ${fontCls} ${className} resize-y leading-snug`.trim();
  return (
    <textarea
      rows={rows}
      readOnly={readOnly || undefined}
      disabled={disabled}
      aria-readonly={readOnly ? 'true' : undefined}
      title={readOnly ? (readOnlyReason || 'This field is read-only.') : undefined}
      className={cls}
      {...rest}
    />
  );
}

// ─── Select ────────────────────────────────────────────────────────
export function Select({
  size = 'md',
  error = false,
  disabled = false,
  className = '',
  children,
  ...rest
}) {
  // Native <select> can't be "read-only" — only disabled. If a caller needs
  // to display an immutable choice, use <Input value={label} readOnly />.
  const cls = `${SHARED_BASE} ${SIZE_CLASS[size]} ${stateClass(error, false)} ${className} appearance-none pr-7 bg-no-repeat bg-[length:12px] bg-[position:right_8px_center]`.trim();
  // SVG caret as a data URL keeps this fully self-contained — no CSS file edit needed.
  const caretBg = {
    backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'><path d='M3 4.5l3 3 3-3' fill='none' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
  };
  return (
    <select
      disabled={disabled}
      className={cls}
      style={caretBg}
      {...rest}
    >
      {children}
    </select>
  );
}

// ─── Label ─────────────────────────────────────────────────────────
export function Label({ children, required = false, htmlFor, className = '' }) {
  return (
    <label
      htmlFor={htmlFor}
      className={`block text-[11px] font-medium uppercase tracking-wide text-v2-ink-secondary mb-1 ${className}`.trim()}
    >
      {children}
      {required && <span className="text-v2-brand ml-0.5" aria-label="required">*</span>}
    </label>
  );
}

// ─── FieldGroup ────────────────────────────────────────────────────
/**
 * Convenience wrapper: Label + control + (hint OR error) below.
 *
 * If `error` is set it takes precedence over `hint` (error is the more
 * urgent signal). Both render in a fixed-height row below the input so
 * a form with mixed error/hint/empty rows stays vertically rhythmic.
 */
export function FieldGroup({
  label,
  required = false,
  hint,
  error,
  htmlFor,
  children,
  className = '',
}) {
  const message = error || hint;
  const messageTone = error ? 'text-v2-brand' : 'text-v2-ink-muted';
  return (
    <div className={`block ${className}`.trim()}>
      {label && <Label htmlFor={htmlFor} required={required}>{label}</Label>}
      {children}
      {message && (
        <div className={`mt-1 text-[11.5px] ${messageTone}`}>{message}</div>
      )}
    </div>
  );
}

export default Input;
